import { PageProps } from 'gatsby';
import { StoryblokStory } from 'gatsby-source-storyblok';
import React, {
  Dispatch,
  SetStateAction,
  memo,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { ContactBarBlokProps } from '../components/ContactBar/ContactBar';
import { DowntimeBannerBlokProps } from '../components/DowntimeBanner/DowntimeBanner';
import { FooterBlokProps } from '../components/Footer/Footer';
import { HeaderBlokProps } from '../components/Header/Header';
import { SharePageBarBlokProps } from '../components/SharePageBar/SharePageBar';
import Head from '../components/Utils/Head/Head';
import { withStoryblokProvider } from '../components/Utils/StoryblokProvider/StoryblokProvider';
import { LayoutContext } from '../contexts/LayoutContext';
import { QuoteStageContext } from '../contexts/QuoteStageContext';
import { Breadcrumbs } from '../interfaces/Breadcrumbs.interface';
import { StoryblokMoveSpecialist } from '../interfaces/StoryblokMoveSpecialist.interface';
import { StoryblokNode } from '../interfaces/StoryblokNode.interface';
import { StoryblokPartnerSite } from '../interfaces/StoryblokPartnerSite.interface';
import Timer from '../utils/Timer/Timer.util';
import {
  DataLayerData,
  pushToDataLayer,
} from '../utils/pushToDataLayer/pushToDataLayer.util';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

export interface StoryblokNodeContextProps {
  layout: {
    header: StoryblokNode<HeaderBlokProps>;
    footer: StoryblokNode<FooterBlokProps>;
    breadcrumbs: Breadcrumbs | null;
    contact_bar: StoryblokNode<ContactBarBlokProps>;
    share_page_bar: StoryblokNode<SharePageBarBlokProps>;
    downtime_banner: StoryblokNode<DowntimeBannerBlokProps>;
  };
  quote_stage_data: {
    partner_sites: StoryblokNode<StoryblokPartnerSite>[];
    move_specialists: StoryblokNode<StoryblokMoveSpecialist>[];
  };
  story: StoryblokNode;
}

const StoryblokNodeTemplate: React.FC<
  PageProps<any, StoryblokNodeContextProps>
> = ({ location, pageContext }) => {
  /*-- injected and abstracted props --*/
  const { SEO, canonical_tag, can_index } = pageContext.story.content;

  /*-- state --*/
  const [scrollPercentage, setScrollPercentage] = useState<number>(0);
  const [
    is25PercentScrollDepthAlreadyAchieved,
    setIs25PercentScrollDepthAlreadyAchieved,
  ] = useState<boolean>(false);
  const [
    is50PercentScrollDepthAlreadyAchieved,
    setIs50PercentScrollDepthAlreadyAchieved,
  ] = useState<boolean>(false);
  const [
    is75PercentScrollDepthAlreadyAchieved,
    setIs75PercentScrollDepthAlreadyAchieved,
  ] = useState<boolean>(false);
  const [
    is90PercentScrollDepthAlreadyAchieved,
    setIs90PercentScrollDepthAlreadyAchieved,
  ] = useState<boolean>(false);
  const [
    is100PercentScrollDepthAlreadyAchieved,
    setIs100PercentScrollDepthAlreadyAchieved,
  ] = useState<boolean>(false);
  const [numberOfMinutesOnPage, setNumberOfMinutesOnPage] = useState<number>(0);

  /*-- mutatable variables --*/
  let interval: number;

  /*-- use effects --*/
  useEffect(() => {
    sessionStorage.setItem('prevURL', location.href);
    getScrollPercentage((scrollPercentage: number) =>
      setScrollPercentage(scrollPercentage)
    );
  }, []);

  /*-- use layout effects --*/
  useLayoutEffect(() => {
    triggerPageViewDataLayerPush();
    initialiseScrollDepthDataLayerPushLifecycle();
    // initialisePageTimerDataLayerPushLifecycle();

    return () => {
      deinitialiseScrollDepthDataLayerPushLifecycle();
      // deinitialisePageTimerDataLayerPushLifecycle();
      resetScrollingStateVariables();
      resetPageTimerStateVariables();
    };
  }, []);

  const triggerPageViewDataLayerPush = (): void => {
    const referrer: string | null = !location.state
      ? document.referrer
      : sessionStorage.getItem('prevURL');

    pushToDataLayer({
      event: 'mhmc_page_view',
      data: {
        location: location.href,
        referrer: referrer || undefined,
      },
    });
  };

  const initialiseScrollDepthDataLayerPushLifecycle = (): void => {
    window.addEventListener('scroll', () => {
      getScrollPercentage((scrollPercentage: number) =>
        setScrollPercentage(scrollPercentage)
      );
    });
  };

  const getScrollPercentage = (
    callback: (scrollPercentage: number) => void
  ): void => {
    const documentScrollTop: number =
      document.body.scrollTop || document.documentElement.scrollTop;
    const documentScrollHeight: number =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;
    const scrollPercentage: number = Math.round(
      (documentScrollTop / documentScrollHeight) * 100
    );
    callback(scrollPercentage);
  };

  /*--
    Due to the asynchronous nature of useState, we need to
    implicitly handle the scroll depth data layer stuff on
    change of the scrollPercentage state variable value change
  --*/
  useEffect(() => {
    handleScrollDepthDataLayerAction(scrollPercentage);
  }, [scrollPercentage]);

  const handleScrollDepthDataLayerAction = (scrollPercentage: number): void => {
    switch (true) {
      case scrollPercentage === 100:
        attemptToSendScrollDepthInfoToDataLayer(
          100,
          is100PercentScrollDepthAlreadyAchieved,
          setIs100PercentScrollDepthAlreadyAchieved
        );
        break;
      case scrollPercentage >= 90:
        attemptToSendScrollDepthInfoToDataLayer(
          90,
          is90PercentScrollDepthAlreadyAchieved,
          setIs90PercentScrollDepthAlreadyAchieved
        );
        break;
      case scrollPercentage >= 75:
        attemptToSendScrollDepthInfoToDataLayer(
          75,
          is75PercentScrollDepthAlreadyAchieved,
          setIs75PercentScrollDepthAlreadyAchieved
        );
        break;
      case scrollPercentage >= 50:
        attemptToSendScrollDepthInfoToDataLayer(
          50,
          is50PercentScrollDepthAlreadyAchieved,
          setIs50PercentScrollDepthAlreadyAchieved
        );
        break;
      case scrollPercentage >= 25:
        attemptToSendScrollDepthInfoToDataLayer(
          25,
          is25PercentScrollDepthAlreadyAchieved,
          setIs25PercentScrollDepthAlreadyAchieved
        );
        break;
      default:
        return;
    }
  };

  const attemptToSendScrollDepthInfoToDataLayer = (
    percent_scrolled: 25 | 50 | 75 | 90 | 100,
    isScrollDepthAlreadyAchieved: boolean,
    setIsScrollDepthAlreadyAchievedState: Dispatch<SetStateAction<boolean>>
  ): void => {
    if (!isScrollDepthAlreadyAchieved) {
      pushToDataLayer({
        event: 'scroll',
        data: {
          percent_scrolled,
        },
      });
      setIsScrollDepthAlreadyAchievedState(true);
    }
  };

  const deinitialiseScrollDepthDataLayerPushLifecycle = (): void => {
    window.removeEventListener('scroll', () => {
      getScrollPercentage((_) => {});
    });
  };

  const { count } = Timer();
  useEffect(() => {
    if (count === 60) {
      pushToDataLayer({
        event: `page_timer_1min`,
        data: {},
      });
    }
    if (count === 180) {
      pushToDataLayer({
        event: `page_timer_3min`,
        data: {},
      });
    }
  }, [count]);

  const resetScrollingStateVariables = (): void => {
    setScrollPercentage(0);
    setIs25PercentScrollDepthAlreadyAchieved(false);
    setIs50PercentScrollDepthAlreadyAchieved(false);
    setIs75PercentScrollDepthAlreadyAchieved(false);
    setIs90PercentScrollDepthAlreadyAchieved(false);
    setIs100PercentScrollDepthAlreadyAchieved(false);
  };

  const resetPageTimerStateVariables = (): void => {
    setNumberOfMinutesOnPage(0);
  };

  const PageContent = () => {
    return (
      <>
        <Head
          seo={SEO}
          canonicalTag={canonical_tag}
          canIndex={can_index}
          location={location}
        />
        <StoryblokStory story={pageContext.story} />
      </>
    );
  };

  if (process.env.GATSBY_RECAPTCHA_SITE_KEY) {
    return withStoryblokProvider(
      <LayoutContext.Provider value={pageContext.layout}>
        <QuoteStageContext.Provider value={pageContext.quote_stage_data}>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.GATSBY_RECAPTCHA_SITE_KEY || ''}
          >
            <Head
              seo={SEO}
              canonicalTag={canonical_tag}
              canIndex={can_index}
              location={location}
            />
            <StoryblokStory story={pageContext.story} />
          </GoogleReCaptchaProvider>
        </QuoteStageContext.Provider>
      </LayoutContext.Provider>
    );
  } else {
    return withStoryblokProvider(
      <LayoutContext.Provider value={pageContext.layout}>
        <QuoteStageContext.Provider value={pageContext.quote_stage_data}>
          <Head
            seo={SEO}
            canonicalTag={canonical_tag}
            canIndex={can_index}
            location={location}
          />
          <StoryblokStory story={pageContext.story} />
        </QuoteStageContext.Provider>
      </LayoutContext.Provider>
    );
  }
};

export default memo(StoryblokNodeTemplate);
